import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Activity,
  ChevronRight,
  Clock,
  MapPin,
  ShieldAlert,
  ShieldCheck,
} from 'lucide-react';
import { env } from '@config/env';
import { startPanicAlarmLoop, stopPanicAlarmLoop } from '@shared/lib/alarm';
import { ErrorState } from '@shared/ui/feedback/ErrorState';
import { Skeleton } from '@shared/ui/feedback/Skeleton';
import {
  fetchActivePanicLogs,
  needsAcknowledgement,
  type PanicLogSummary,
} from './api';

/* ── Time helpers ────────────────────────────────────────────────────── */

function formatElapsed(ms: number): string {
  if (ms < 1000) return 'just now';
  if (ms < 60_000) return `${Math.floor(ms / 1000)}s`;
  if (ms < 3_600_000) {
    const m = Math.floor(ms / 60_000);
    const s = Math.floor((ms % 60_000) / 1000);
    return s > 0 ? `${m}m ${s}s` : `${m}m`;
  }
  if (ms < 86_400_000) {
    const h = Math.floor(ms / 3_600_000);
    const m = Math.floor((ms % 3_600_000) / 60_000);
    return `${h}h ${m}m`;
  }
  return `${Math.floor(ms / 86_400_000)}d`;
}

/** Tone the elapsed-time chip from bright-red (fresh) to neutral (stale). */
function elapsedTone(ms: number): string {
  if (ms < 60_000) return 'bg-red-600 text-white';
  if (ms < 300_000) return 'bg-red-100 text-red-700';
  if (ms < 1_800_000) return 'bg-amber-100 text-amber-800';
  return 'bg-neutral-100 text-neutral-700';
}

function statusLabel(status: string): string {
  return status.replace(/_/g, ' ');
}

/* ── KPI tile ────────────────────────────────────────────────────────── */

interface TileProps {
  label: string;
  value: string | number;
  tone: 'red' | 'amber' | 'blue' | 'neutral';
  pulse?: boolean;
  hint?: string;
}

function Tile({ label, value, tone, pulse, hint }: TileProps): JSX.Element {
  const surface = {
    red: 'border-red-200 bg-red-50 text-red-900',
    amber: 'border-amber-200 bg-amber-50 text-amber-900',
    blue: 'border-blue-200 bg-blue-50 text-blue-900',
    neutral: 'border-neutral-200 bg-white text-neutral-900',
  }[tone];

  const dot = {
    red: 'bg-red-500',
    amber: 'bg-amber-500',
    blue: 'bg-blue-500',
    neutral: 'bg-neutral-400',
  }[tone];

  return (
    <div
      className={`relative rounded-xl border p-4 transition-colors ${surface} ${
        pulse ? 'animate-pulse' : ''
      }`}
    >
      <div className="flex items-center gap-2">
        <span className={`h-2 w-2 rounded-full ${dot}`} />
        <p className="text-[10px] font-bold uppercase tracking-wider opacity-70">
          {label}
        </p>
      </div>
      <p className="mt-2 text-4xl font-extrabold tabular-nums leading-none">
        {value}
      </p>
      {hint ? (
        <p className="mt-1.5 text-[11px] font-medium opacity-60">{hint}</p>
      ) : null}
    </div>
  );
}

/* ── Incident card ───────────────────────────────────────────────────── */

interface IncidentCardProps {
  log: PanicLogSummary;
  urgent: boolean;
  now: number;
}

function IncidentCard({ log, urgent, now }: IncidentCardProps): JSX.Element {
  const created = new Date(log.created_at).getTime();
  const ms = Number.isNaN(created) ? 0 : Math.max(0, now - created);
  const hasCoords = !!(log.latitude && log.longitude);

  return (
    <Link
      to={`/incidents/${log.panic_log_id}`}
      className={`group relative block overflow-hidden rounded-xl border bg-white transition-all ${
        urgent
          ? 'border-red-300 shadow-sm hover:border-red-500 hover:shadow-md'
          : 'border-neutral-200 hover:border-neutral-300 hover:bg-neutral-50'
      }`}
    >
      {/* Severity stripe */}
      <span
        className={`absolute inset-y-0 left-0 w-1.5 ${
          urgent ? 'bg-red-500' : 'bg-amber-400'
        }`}
      />

      <div className="flex items-start justify-between gap-3 py-4 pl-5 pr-4">
        <div className="min-w-0 flex-1">
          {/* Chips row */}
          <div className="flex flex-wrap items-center gap-1.5">
            <span
              className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide tabular-nums ${elapsedTone(
                ms,
              )}`}
            >
              <Clock className="h-3 w-3" strokeWidth={2.5} />
              {formatElapsed(ms)}
            </span>
            <span className="rounded-md bg-neutral-100 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-neutral-700">
              {statusLabel(String(log.status))}
            </span>
            {hasCoords ? (
              <span className="inline-flex items-center gap-1 rounded-md bg-blue-100 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-blue-700">
                <MapPin className="h-3 w-3" strokeWidth={2.5} />
                Located
              </span>
            ) : null}
          </div>

          {/* Title + member */}
          <p className="mt-2.5 text-lg font-bold leading-tight text-ink">
            {log.emergency_type_name}
          </p>
          <p className="mt-0.5 truncate text-sm font-medium text-neutral-600">
            {log.triggered_by_name?.trim() || 'Unknown member'}
          </p>
        </div>

        {/* Right-side action */}
        <div className="flex shrink-0 flex-col items-end gap-2">
          {urgent ? (
            <span className="inline-flex items-center gap-1 rounded-md bg-red-600 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide text-white shadow-sm">
              Acknowledge
              <ChevronRight className="h-3.5 w-3.5" strokeWidth={2.5} />
            </span>
          ) : (
            <ChevronRight className="h-5 w-5 text-neutral-400 transition-transform group-hover:translate-x-0.5" />
          )}
        </div>
      </div>
    </Link>
  );
}

/* ── Dashboard ───────────────────────────────────────────────────────── */

export function NocDashboard(): JSX.Element {
  // 1-second tick — drives the elapsed-time labels between polling refreshes.
  const [now, setNow] = useState<number>(() => Date.now());
  useEffect(() => {
    const h = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(h);
  }, []);

  const { data, isLoading, isError, error, refetch, isFetching, dataUpdatedAt } = useQuery({
    queryKey: ['panic', 'active'],
    queryFn: fetchActivePanicLogs,
    refetchInterval: env.panicPollIntervalMs,
  });

  const logs = data ?? [];
  const needsAck = logs.filter(needsAcknowledgement);
  const inProgress = logs.filter((l) => !needsAcknowledgement(l));
  const hasUnacked = needsAck.length > 0;

  useEffect(() => {
    if (hasUnacked) startPanicAlarmLoop();
    else stopPanicAlarmLoop();
    return () => stopPanicAlarmLoop();
  }, [hasUnacked]);

  const lastAlertMs =
    logs.length > 0
      ? Math.max(0, now - new Date(logs[0]!.created_at).getTime())
      : null;
  const lastRefreshMs = dataUpdatedAt ? Math.max(0, now - dataUpdatedAt) : null;

  return (
    <div className="mx-auto max-w-5xl px-4 py-5">
      {/* ── Live status banner ──────────────────────────────────────── */}
      <div className="mb-5 flex items-center justify-between gap-3 rounded-lg border border-neutral-200 bg-white px-4 py-2.5">
        <div className="flex items-center gap-2">
          <span className="relative flex h-2.5 w-2.5">
            <span
              className={`absolute inset-0 rounded-full opacity-60 ${
                hasUnacked ? 'animate-ping bg-red-500' : 'animate-ping bg-green-500'
              }`}
            />
            <span
              className={`relative h-2.5 w-2.5 rounded-full ${
                hasUnacked ? 'bg-red-500' : 'bg-green-500'
              }`}
            />
          </span>
          <p className="text-xs font-bold uppercase tracking-wider text-ink">
            {hasUnacked ? 'Active emergency' : 'Monitoring'}
          </p>
        </div>
        <div className="flex items-center gap-2 text-[11px] font-medium text-neutral-500">
          {lastRefreshMs !== null ? (
            <span className="tabular-nums">
              {isFetching ? 'Updating…' : `Refreshed ${formatElapsed(lastRefreshMs)} ago`}
            </span>
          ) : null}
          <span className="text-neutral-300">·</span>
          <span>Poll every {env.panicPollIntervalMs / 1000}s</span>
        </div>
      </div>

      {/* ── KPI tiles ───────────────────────────────────────────────── */}
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Tile
          label="Awaiting Ack"
          value={needsAck.length}
          tone="red"
          pulse={hasUnacked}
          hint={hasUnacked ? 'Action required' : 'All clear'}
        />
        <Tile
          label="Active"
          value={logs.length}
          tone={logs.length > 0 ? 'amber' : 'neutral'}
          hint="Open incidents"
        />
        <Tile
          label="In Progress"
          value={inProgress.length}
          tone="blue"
          hint="Acknowledged"
        />
        <Tile
          label="Last Alert"
          value={lastAlertMs !== null ? formatElapsed(lastAlertMs) : '—'}
          tone="neutral"
          hint={logs.length > 0 ? 'ago' : 'None active'}
        />
      </div>

      {/* ── Loading / error ─────────────────────────────────────────── */}
      {isLoading && (
        <div className="space-y-3">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      )}

      {isError && (
        <ErrorState
          title="Could not load incidents"
          description={error instanceof Error ? error.message : 'Request failed'}
          onRetry={() => void refetch()}
        />
      )}

      {/* ── All clear ───────────────────────────────────────────────── */}
      {!isLoading && !isError && logs.length === 0 && (
        <div className="rounded-xl border-2 border-dashed border-green-200 bg-green-50/40 px-6 py-12 text-center">
          <ShieldCheck className="mx-auto h-12 w-12 text-green-500" strokeWidth={1.5} />
          <h2 className="mt-3 text-xl font-bold text-green-900">All clear</h2>
          <p className="mt-1 text-sm font-medium text-green-700">
            No active panic incidents. The board refreshes every{' '}
            {env.panicPollIntervalMs / 1000} seconds.
          </p>
        </div>
      )}

      {/* ── Awaiting acknowledgement ────────────────────────────────── */}
      {!isLoading && !isError && needsAck.length > 0 && (
        <section className="mb-5">
          <div className="mb-3 flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-red-600" strokeWidth={2} />
            <h2 className="text-sm font-bold uppercase tracking-wider text-red-700">
              Awaiting acknowledgement
            </h2>
            <span className="rounded-md bg-red-600 px-2 py-0.5 text-xs font-bold text-white tabular-nums">
              {needsAck.length}
            </span>
          </div>
          <div className="space-y-3">
            {needsAck.map((log) => (
              <IncidentCard key={log.panic_log_id} log={log} urgent now={now} />
            ))}
          </div>
        </section>
      )}

      {/* ── In progress ─────────────────────────────────────────────── */}
      {!isLoading && !isError && inProgress.length > 0 && (
        <section>
          <div className="mb-3 flex items-center gap-2">
            <Activity className="h-5 w-5 text-amber-600" strokeWidth={2} />
            <h2 className="text-sm font-bold uppercase tracking-wider text-amber-700">
              In progress
            </h2>
            <span className="rounded-md bg-amber-500 px-2 py-0.5 text-xs font-bold text-white tabular-nums">
              {inProgress.length}
            </span>
          </div>
          <div className="space-y-3">
            {inProgress.map((log) => (
              <IncidentCard key={log.panic_log_id} log={log} urgent={false} now={now} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
